import type { Ref } from 'vue';

interface ModalLifecycleConfig {
	isOpen: Ref<boolean>;
	focusCallback: () => void;
	cleanupCallback?: () => void;
}

/**
 * Manages modal lifecycle behaviors:
 * - Prevents body scroll when modal is open
 * - Detects keyboard height on mobile
 * - Handles focus management
 * - Cleans up on unmount
 */
export function useModalLifecycle({
	isOpen,
	focusCallback,
	cleanupCallback
}: ModalLifecycleConfig) {
	const keyboardHeight = ref<number>(0);
	const initialViewportHeight = ref<number>(0);
	let savedScrollPosition = 0;
	let originalHtmlOverflow = '';

	/**
	 * Prevent body scroll and save scroll position
	 */
	function preventBodyScroll() {
		savedScrollPosition = window.scrollY;
		originalHtmlOverflow = document.documentElement.style.overflow;
		document.documentElement.style.overflow = 'hidden';
		document.body.classList.add('scroll-locked');
		document.body.style.top = `-${savedScrollPosition}px`;
	}

	/**
	 * Restore body scroll to previous position
	 */
	function restoreBodyScroll() {
		document.documentElement.style.overflow = originalHtmlOverflow;
		document.body.classList.remove('scroll-locked');
		document.body.style.removeProperty('position');
		document.body.style.removeProperty('top');
		document.body.style.removeProperty('width');
		document.body.style.overflow = '';
		document.body.style.height = '';
		document.body.style.touchAction = '';
		requestAnimationFrame(() => {
			window.scrollTo(0, savedScrollPosition);
		});
	}

	/**
	 * Handle viewport resize to detect keyboard height
	 */
	function handleKeyboardResize() {
		if (!window.visualViewport) return;
		const currentHeight = window.visualViewport.height;
		const heightDiff = initialViewportHeight.value - currentHeight;
		keyboardHeight.value = heightDiff > 150 ? heightDiff : 0;
	}

	/**
	 * Setup keyboard height detection
	 */
	function setupKeyboardHandling() {
		if (!window.visualViewport) return;
		initialViewportHeight.value = window.visualViewport.height;
		window.visualViewport.addEventListener('resize', handleKeyboardResize);
	}

	/**
	 * Cleanup keyboard height detection
	 */
	function cleanupKeyboardHandling() {
		if (!window.visualViewport) return;
		window.visualViewport.removeEventListener('resize', handleKeyboardResize);
		keyboardHeight.value = 0;
	}

	/**
	 * Watch modal open state and manage lifecycle
	 */
	watch(isOpen, newValue => {
		if (newValue) {
			preventBodyScroll();
			setupKeyboardHandling();
		} else {
			restoreBodyScroll();
			cleanupKeyboardHandling();
		}
	});

	/**
	 * Cleanup on unmount
	 */
	onBeforeUnmount(() => {
		if (isOpen.value) {
			restoreBodyScroll();
		}
		cleanupKeyboardHandling();
		cleanupCallback?.();
	});

	return {
		keyboardHeight: readonly(keyboardHeight)
	};
}
